import React, { useEffect, useState, useContext } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Grid,
} from '@material-ui/core';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import api, { consoleDevLog } from '../../apiConfig';
import { ContactsContext } from '../ContactsContext';
import ContactForm from '../ContactForm';
import '../../assets/styles/Step4/GenerateModal.css';

const GenerateModal = ({
  open,
  onClose,
  cmaData = {},
  adjustments,
  selectedHomes,
  finalSelectedHomes,
  priceRange,
  realtorComments,
  subjectProperty,
  updateClientResources,
  history,
  shouldRedirect = false,
}) => {
  const { contacts, addContact } = useContext(ContactsContext);
  const [cmaName, setCmaName] = useState(cmaData.cmaName || '');
  const [selectedContact, setSelectedContact] = useState(null);
  const [realtorInfo, setRealtorInfo] = useState({
    fullName: 'Your Realtor',
    title: 'Real Estate Agent',
    contact: '647-345-1284',
    email: 'example@gmail.com',
    brokerageInfo: 'Homelife G1 Realty',
    brokerageAddress: '2260 Bovaird Dr E #202, Brampton, ON L6R 0W3',
  });

  const [openAddClientModal, setOpenAddClientModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [finishDownloadLoading, setFinishDownloadLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const mergedAdjustments = adjustments ?? cmaData.adjustments ?? {};
  const mergedPriceRange = priceRange ?? cmaData.priceRange ?? {};
  const mergedSelectedHomes = selectedHomes ?? cmaData.selectedHomes ?? [];
  const mergedFinalSelectedHomes = finalSelectedHomes ?? cmaData.finalSelectedHomes ?? [];
  const mergedRealtorComments = realtorComments ?? cmaData.realtorComments ?? '';
  const mergedSubjectProperty = subjectProperty ?? cmaData.subjectProperty ?? {};

  useEffect(() => {
    const fetchRealtorInfo = async () => {
      try {
        const response = await api.get('/realtor/info');
        setRealtorInfo(response.data);
      } catch (error) {
        console.error('Error fetching realtor info:', error);
      }
    };
    fetchRealtorInfo();
  }, []);

  useEffect(() => {
    if (cmaData.client && contacts.length) {
      const found = contacts.find(
        (c) => `${c.firstName} ${c.lastName}`.toLowerCase() === cmaData.client.toLowerCase()
      );
      if (found) setSelectedContact(found);
    }
  }, [cmaData.client, contacts]);

  useEffect(() => {
    if (cmaData.cmaName) setCmaName(cmaData.cmaName);
  }, [cmaData.cmaName]);

  useEffect(() => {
    if (open) {
      setPdfUrl(null);
      generateReport(true);
    }
  }, [open]);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);
  const handleClose = () => { setPdfUrl(null); onClose(); };

  const handleClientChange = (event) => {
    const val = event.target.value;
    if (val === 'ADD_NEW_CLIENT') {
      setOpenAddClientModal(true);
      return;
    }
    const found = contacts.find((c) => c.id === val);
    setSelectedContact(found || null);
  };

  const handleAddContact = (data) => {
    const id = Date.now().toString();
    const contact = { id, ...data };
    addContact(contact);
    setSelectedContact(contact);
    setOpenAddClientModal(false);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Generate CMA PDF
  const generateReport = async (isPreview = false) => {
    try {
      const endpoint = '/report/generate';
      const relevanceScores = (selectedHomes ?? []).reduce((acc, h) => {
        acc[h.Ml_num] = h.score;
        return acc;
      }, {});
      const uploadToS3 = !cmaData || (Object.keys(cmaData).length === 0 && cmaData.constructor === Object);

      // Choose the first custom image URL if available, else fallback to single image
      const imageUrl =
        mergedSubjectProperty.imageUrls?.[0] ||
        mergedSubjectProperty.image ||
        '';

      // Build subjectProperty payload matching the API model
      const {
        A_c,
        Addr,
        Bath_tot,
        Br,
        Bsmt1_out,
        Dom,
        Latitude,
        Longitude,
        Lp_dol,
        Lsc,
        Ml_num,
        Orig_dol,
        Sp_dol,
        Sqft,
        Tot_park_spcs,
        Yr_built,
        full_address,
      } = mergedSubjectProperty;

      const subjectData = {
        A_c: A_c || '',
        Addr: Addr || full_address || '',
        Bath_tot: Bath_tot || 0,
        Br: Br || 0,
        Bsmt1_out: Bsmt1_out || '',
        Dom: Dom || 0,
        Latitude: Latitude || 0,
        Longitude: Longitude || 0,
        Lp_dol: Lp_dol ?? null,
        Lsc: Lsc || '',
        Ml_num: Ml_num || '',
        Orig_dol: Orig_dol || String(Lp_dol || ''),
        Sp_dol: Sp_dol ?? null,
        Sqft: Sqft || null,
        Tot_park_spcs: Tot_park_spcs || 0,
        Yr_built: Yr_built || null,
        full_address: full_address || Addr || '',
        image: imageUrl,
      };

      const payload = {
        clientId: selectedContact?.id || cmaData.clientId || '',
        cmaName,
        realtorId: selectedContact?.id || undefined,
        realtorName: realtorInfo.fullName,
        realtorTitle: realtorInfo.title,
        realtorContact: realtorInfo.contact,
        realtorEmail: realtorInfo.email,
        brokerageInfo: realtorInfo.brokerageInfo,
        brokerageAddress: realtorInfo.brokerageAddress,
        subjectProperty: subjectData,
        realtorComments: mergedRealtorComments,
        adjustments: mergedAdjustments,
        relevanceScores,
        priceRange: mergedPriceRange,
        preview: isPreview,
        uploadToS3,
      };

      consoleDevLog('Sending report payload', payload);

      const response = await api.post(endpoint, payload, { responseType: 'blob' });
      const ct = response.headers['content-type'];
      if (ct !== 'application/pdf') {
        const txt = await response.data.text();
        console.error('Gen error:', txt);
        alert('Failed to generate report.');
        return null;
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      return url;
    } catch (err) {
      console.error('Error generating report:', err);
      alert('An error occurred while generating the report.');
      return null;
    }
  };

  // Create or update CMA record in the backend
  const handleSaveCMARecord = async () => {
    // Build final dictionary of relevanceScores from finalSelectedHomes
    const relevanceScores = mergedFinalSelectedHomes.reduce((acc, home) => {
      acc[home.Ml_num] = home.score;
      return acc;
    }, {});

    const newCMA = {
      clientId: selectedContact?.id || cmaData.clientId || '',
      cmaName,
      realtorName: realtorInfo.fullName,
      realtorTitle: realtorInfo.title,
      realtorContact: realtorInfo.contact,
      realtorEmail: realtorInfo.email,
      brokerageInfo: realtorInfo.brokerageInfo,
      brokerageAddress: realtorInfo.brokerageAddress,
      client: selectedContact
        ? `${selectedContact.firstName} ${selectedContact.lastName}`
        : '',
      subjectPropMlsNum: mergedSubjectProperty.Ml_num || '',
      subjectProperty: mergedSubjectProperty,
      priceRange: mergedPriceRange,
      realtorComments: mergedRealtorComments,
      adjustments: mergedAdjustments,
      relevanceScores,
      finalSelectedHomes: mergedFinalSelectedHomes,
      selectedHomes: mergedSelectedHomes,
    };

    // If we have an existing cmaData.id, update. Otherwise create
    if (cmaData.id) {
      await api.put(`/cma/${cmaData.id}`, newCMA);
    } else {
      await api.post('/cma', newCMA);
    }

    // Optionally update client resources
    if (selectedContact && updateClientResources) {
      const newResourceLink = `Link_to_CMA_${Date.now()}`;
      updateClientResources(
        `${selectedContact.firstName} ${selectedContact.lastName}`,
        newResourceLink
      );
    }
  };

  // Finish CMA (no download)
  const handleFinishCMA = async () => {
    setValidationTriggered(true);
    if (!cmaName || !selectedContact) return;

    try {
      setFinishLoading(true);
      // 1) Save or update CMA in the backend
      await handleSaveCMARecord();
      // 2) Optionally generate the PDF to store it (remove if not needed)
      await generateReport(false);
      // 3) Redirect if desired
      if (shouldRedirect && history) {
        history.push({
          pathname: '/smart-cma',
          state: { cmaCreated: true },
        });
      }
    } catch (error) {
      console.error('Error finishing CMA:', error);
    } finally {
      setFinishLoading(false);
      handleClose();
    }
  };

  // Finish CMA & Download PDF
  const handleFinishAndDownloadPDF = async () => {
    setValidationTriggered(true);
    if (!cmaName || !selectedContact) return;

    try {
      setFinishDownloadLoading(true);
      // 1) Save or update CMA in the backend
      await handleSaveCMARecord();
      // 2) Generate PDF
      const pdfBlobUrl = await generateReport(false);
      // 3) Download PDF if we have it
      if (pdfBlobUrl) {
        const link = document.createElement('a');
        link.href = pdfBlobUrl;
        link.download = `${cmaName || 'CMA_Report'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to download the PDF. Please try again.');
      }
      // 4) Redirect if needed
      if (shouldRedirect && history) {
        history.push({
          pathname: '/smart-cma',
          state: { cmaCreated: true },
        });
      }
    } catch (error) {
      console.error('Error finishing & downloading CMA:', error);
    } finally {
      setFinishDownloadLoading(false);
      handleClose();
    }
  };

  // Convert an ArrayBuffer to Base64 (used in handleSendEmail)
  function arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Email the PDF
  const handleSendEmail = async () => {
    setValidationTriggered(true);
    if (!recipientEmail || !isValidEmail(recipientEmail)) {
      return;
    }

    try {
      setEmailLoading(true);
      // 1) Save your CMA record
      await handleSaveCMARecord();
      // 2) Generate the PDF
      const pdfBlobUrl = await generateReport(false);
      if (!pdfBlobUrl) {
        alert('Failed to generate the PDF. Please try again.');
        return;
      }
      // 3) Convert PDF blob to Base64
      const pdfBlob = await fetch(pdfBlobUrl).then((r) => r.blob());
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      const pdfBase64 = arrayBufferToBase64(pdfArrayBuffer);
      // 4) Send email request
      await api.post('/report/sendEmail', {
        recipientEmail,
        emailSubject,
        emailBody,
        cmaName,
        pdfBase64,
      });
      // 5) Redirect if needed
      if (shouldRedirect && history) {
        history.push({
          pathname: '/smart-cma',
          state: { cmaCreated: true },
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('An error occurred while sending the email.');
    } finally {
      setEmailLoading(false);
      handleClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modal-box">
        <Typography variant="h4" className="modal-title">
          Generate CMA
        </Typography>

        {/* CMA Title & Client in one row */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="CMA Title"
              variant="outlined"
              fullWidth
              value={cmaName}
              onChange={(e) => setCmaName(e.target.value)}
              margin="normal"
              error={validationTriggered && !cmaName}
              helperText={
                validationTriggered && !cmaName ? 'Title is required' : null
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client"
              variant="outlined"
              fullWidth
              select
              value={selectedContact ? selectedContact.id : ''}
              onChange={handleClientChange}
              margin="normal"
              error={validationTriggered && !selectedContact}
              helperText={
                validationTriggered && !selectedContact
                  ? 'Client is required'
                  : null
              }
            >
              {contacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.id}>
                  {contact.firstName} {contact.lastName}
                </MenuItem>
              ))}
              <MenuItem value="ADD_NEW_CLIENT" className="add-new-menu-item">
                + New Client
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {/* Tabs for Download vs. Email */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          className="custom-tabs"
        >
          <Tab label="Download" />
          <Tab label="Email" />
        </Tabs>

        {/* Tab Content: Download Tab */}
        {activeTab === 0 && (
          <Box className="tab-content">
            <Paper className="pdf-preview">
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              {pdfUrl ? (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
                  <Viewer fileUrl={pdfUrl} />
                </Worker>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Loading...
                </Typography>
              )}
            </Paper>

            <Box className="action-buttons">
              <Button onClick={handleClose} color="secondary" className="cancel-button">
                Close
              </Button>

              {/* Finish (no download) */}
              <Button
                onClick={handleFinishCMA}
                variant="contained"
                disabled={finishLoading || finishDownloadLoading}
                style={{ marginRight: '0.5rem' }}
              >
                {finishLoading ? <CircularProgress size={24} /> : 'Finish'}
              </Button>

              {/* Finish & Download PDF */}
              <Button
                onClick={handleFinishAndDownloadPDF}
                variant="contained"
                color="primary"
                disabled={finishDownloadLoading || finishLoading}
              >
                {finishDownloadLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Finish & Download PDF'
                )}
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab Content: Email Tab */}
        {activeTab === 1 && (
          <Box className="tab-content">
            <TextField
              label="To"
              variant="outlined"
              fullWidth
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              margin="normal"
              error={
                validationTriggered &&
                (!recipientEmail || !isValidEmail(recipientEmail))
              }
              helperText={
                validationTriggered && !recipientEmail
                  ? 'Email is required'
                  : validationTriggered &&
                    recipientEmail &&
                    !isValidEmail(recipientEmail)
                  ? 'Please enter a valid email address'
                  : null
              }
            />
            <TextField
              label="Subject (Optional)"
              variant="outlined"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Body (Optional)"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              margin="normal"
            />

            <Paper className="pdf-preview">
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              {pdfUrl ? (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
                  <Viewer fileUrl={pdfUrl} />
                </Worker>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Loading...
                </Typography>
              )}
            </Paper>

            <Box className="action-buttons">
              <Button onClick={handleClose} color="secondary" className="cancel-button">
                Close
              </Button>
              <Button
                onClick={handleSendEmail}
                variant="contained"
                color="primary"
                disabled={emailLoading}
              >
                {emailLoading ? <CircularProgress size={24} /> : 'Send'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Add New Client Modal */}
        <Modal
          open={openAddClientModal}
          onClose={() => setOpenAddClientModal(false)}
        >
          <Box className="modal-box">
            <Typography variant="h6" gutterBottom>
              New Client
            </Typography>
            <ContactForm
              setView={() => {}}
              closeForm={() => setOpenAddClientModal(false)}
              addContact={handleAddContact}
            />
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
};

export default GenerateModal;
